import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Tooltip, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { DataTable, DataTableActions, DataTableColumn, SocialIcon } from 'components';
import { ExternalShare, ExternalSharesSortFields, SortDirections } from 'api';
import { InfiniteQuery } from 'types/app';

import { Actions } from './index';
import UrlMetadata from './UrlMetadata';
import ShareImage from './ShareImage';

import { IMAGE_SIZE } from 'features/admin/constants';
import { ExternalSharesSortParams, SearchContentFilter } from 'features/admin/types';
import { UpdateExternalShareMutationOptions } from './Actions';

interface Props extends InfiniteQuery<ExternalShare> {
  filter: SearchContentFilter;
  updateExternalSharesMutationOptions: UpdateExternalShareMutationOptions;
  sortBy: ExternalSharesSortParams;
  onSort: (sort: ExternalSharesSortParams[]) => void;
}

const Table: FC<Props> = ({
  results,
  isExhausted,
  isInitialLoad,
  sentinelRef,
  updateExternalSharesMutationOptions,
  sortBy,
  onSort,
}) => {
  const { t } = useTranslation();

  const columns: DataTableColumn<ExternalShare>[] = [
    {
      width: IMAGE_SIZE + 50,
      headerCell: {
        render: t('admin:sharePreview'),
      },
      bodyCell: {
        render: share => <ShareImage {...share} />,
      },
    },
    {
      width: 350,
      headerCell: {
        render: '',
      },
      bodyCell: {
        render: share => <UrlMetadata {...share} />,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        render: share => <SocialIcon provider={share.network} />,
      },
    },
    {
      width: 250,
      headerCell: {
        render: t('admin:userEmail'),
      },
      bodyCell: {
        render: share => (
          <Tooltip title={share.user_email}>
            <Typography noWrap>{share.user_email}</Typography>
          </Tooltip>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        sort: {
          field: ExternalSharesSortFields.UserReach,
          direction: SortDirections.Desc,
        },
        render: t('admin:userReach'),
      },
      bodyCell: {
        render: share => <Typography>{share.user_reach}</Typography>,
      },
    },
    {
      width: 120,
      headerCell: {
        sort: {
          field: ExternalSharesSortFields.EngagementCount,
          direction: SortDirections.Desc,
        },
        render: t('common:engagement_plural'),
      },
      bodyCell: {
        render: share => <Typography>{share.engagement_count}</Typography>,
      },
    },
    {
      width: 100,
      headerCell: {
        sort: {
          field: ExternalSharesSortFields.FirstEngagedAt,
          direction: SortDirections.Desc,
        },
        render: t('common:date'),
      },
      bodyCell: {
        render: share => {
          const mom = moment.unix(share.first_engaged_at);

          return (
            <Typography noWrap title={mom.format('LL')}>
              {mom.format('l')}
            </Typography>
          );
        },
      },
    },
    {
      width: 140,
      headerCell: {
        render: t('admin:companyPages'),
      },
      bodyCell: {
        render: share => (share.linkedin_page_share ? <CheckRoundedIcon /> : null),
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<ExternalShare> = share => {
    return (
      <Actions
        updateExternalSharesMutationOptions={updateExternalSharesMutationOptions}
        {...share}
      />
    );
  };

  return (
    <DataTable
      pagination={pagination}
      data={results}
      columns={columns}
      actions={actions}
      rowSelection={null}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default Table;
